import { useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'

import { CreationCard } from 'modules/cost_centers/components/CostCenterAssigments/components/Cards/CreationCard'
import useCostCentersAssignments from 'modules/cost_centers/hooks/useCostCentersAssignments'

import { Content } from './components/Content'
import { Loading } from './components/Loading'

interface Props {
  loading?: boolean
  resourceId: string
  resourceType: string
  withAssignmentIdOpened?: string
  canManageAssignments?: boolean
  children?: React.ReactNode
  skipQueries?: boolean
  withNewAssignment?: boolean
  onEmptyAssignmentsCancel?: () => void
  onMutationCancel?: () => void
  onDelete?: () => void
  onSave?: () => void
  onToggleEdit?: (value: boolean) => void
  onShowEmptyState?: (showEmptyState: boolean) => void
  refetchQueries?: TypedDocumentNode[]
}

export const Assignments = ({
  loading,
  resourceId,
  resourceType,
  withAssignmentIdOpened,
  canManageAssignments = true,
  skipQueries,
  withNewAssignment,
  onEmptyAssignmentsCancel,
  onMutationCancel,
  onDelete,
  onToggleEdit,
  onSave,
  onShowEmptyState,
  refetchQueries,
}: Props) => {
  const { hasNewStructureEnabled } = useOutletContext<{
    hasNewStructureEnabled: boolean
  }>()

  const {
    isLoading: assignmentsLoading,
    assignments,
    refetch,
  } = useCostCentersAssignments({
    resourceId,
    resourceType,
    skipQueries,
  })

  // Notify parent when showing empty state (for new structure with no assignments)
  const showEmptyState = hasNewStructureEnabled && assignments.length === 0
  useEffect(() => {
    onShowEmptyState?.(showEmptyState)
  }, [showEmptyState, onShowEmptyState])

  const refetchAssignments = async () => {
    return await refetch()
  }

  const handleEmptyAssignmentsCancel = () => {
    onEmptyAssignmentsCancel?.()
  }

  const handleMutationCancel = () => {
    onMutationCancel?.()
  }

  const handleDelete = async () => {
    await refetchAssignments()

    onDelete?.()
  }

  const handleSave = async () => {
    await refetchAssignments()

    onSave?.()
  }

  const handleToggleEdit = (value: boolean) => {
    onToggleEdit?.(value)
  }

  if (assignmentsLoading || loading) {
    return <Loading />
  }

  return (
    <Box gap="s24">
      <Content
        assignments={assignments}
        resourceId={resourceId}
        resourceType={resourceType}
        withAssignmentIdOpened={withAssignmentIdOpened}
        canManageAssignments={canManageAssignments}
        onCreationCancel={handleMutationCancel}
        onDelete={handleDelete}
        onSave={handleSave}
        onToggleEdit={handleToggleEdit}
        refetchQueries={refetchQueries}
      >
        {canManageAssignments && withNewAssignment && (
          <CreationCard
            allAssignments={assignments}
            resourceId={resourceId}
            resourceType={resourceType}
            onCancel={handleEmptyAssignmentsCancel}
            onSave={handleSave}
            refetchQueries={refetchQueries}
          />
        )}
      </Content>
    </Box>
  )
}
